import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { orgsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Building2, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_e3b3c70d-c72e-49dc-b41a-da6affdc6e46/artifacts/4w4rfom9_IMG_0777.PNG";

export default function OrgSignupPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteToken = searchParams.get('invite');

  const [checking, setChecking] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');

  const [orgName, setOrgName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function check() {
      if (!inviteToken) {
        setError('Missing invitation token. Please scan the QR code provided by your Visitex contact.');
        setChecking(false);
        return;
      }
      try {
        const { data } = await orgsApi.validateInvitation(inviteToken);
        if (cancelled) return;
        setInvite(data);
        setOrgName(data?.suggested_org_name || '');
        setContactEmail(data?.contact_email || '');
      } catch (e) {
        if (cancelled) return;
        setError(e.response?.data?.detail || 'Invitation is no longer valid');
      } finally {
        if (!cancelled) setChecking(false);
      }
    }
    check();
    return () => { cancelled = true; };
  }, [inviteToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await orgsApi.signup({
        token: inviteToken,
        org_name: orgName.trim(),
        contact_email: contactEmail.trim() || undefined,
        admin_name: adminName.trim(),
        admin_email: adminEmail.trim(),
        password,
      });
      localStorage.setItem('token', data.token);
      toast.success(`Welcome to Visitex, ${data.organisation.name}!`);
      // Reload so AuthContext picks up the new token from localStorage
      window.location.href = '/admin';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Signup failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 relative overflow-hidden">
      {/* Visitex glowing blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-teal-500/20 blur-3xl"></div>
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-emerald-500/20 blur-3xl"></div>
        <div className="absolute top-1/3 left-1/2 w-72 h-72 rounded-full bg-cyan-500/15 blur-3xl"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl bg-slate-800/90 border-slate-700 backdrop-blur-sm">
          <CardHeader className="space-y-3 text-center">
            <div className="flex justify-center">
              <img src={LOGO_URL} alt="Visitex" className="w-16 h-16 rounded-full ring-2 ring-teal-400/40" />
            </div>
            <CardTitle className="text-2xl text-white" data-testid="org-signup-title">
              Welcome to Visitex
            </CardTitle>
            <CardDescription className="text-slate-300">
              Set up your organisation account
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {checking && (
              <div className="flex items-center justify-center py-12 text-slate-300">
                <Loader2 className="w-6 h-6 animate-spin mr-2" /> Verifying your invitation…
              </div>
            )}

            {!checking && error && (
              <div className="bg-rose-500/15 border border-rose-500/40 rounded-md p-4 text-rose-100 flex gap-3 items-start" data-testid="org-signup-error">
                <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                <div className="space-y-2">
                  <p className="font-medium">{error}</p>
                  <p className="text-sm text-rose-200/80">
                    Ask Visitex for a fresh QR code or invitation link to proceed.
                  </p>
                  <Link to="/login" className="inline-block text-sm underline text-rose-100">Back to login</Link>
                </div>
              </div>
            )}

            {!checking && !error && invite && (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="org-signup-form">
                <div className="bg-teal-500/10 border border-teal-400/30 rounded-md p-3 flex items-start gap-3 text-teal-100 text-sm">
                  <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0" />
                  <div>
                    Invitation verified. Complete the form to create your own
                    isolated Visitex workspace.
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2 space-y-1">
                    <Label className="text-slate-200" htmlFor="org-name">
                      <Building2 className="w-4 h-4 inline mr-1" /> Organisation name
                    </Label>
                    <Input
                      id="org-name"
                      data-testid="org-name-input"
                      value={orgName}
                      onChange={(e) => setOrgName(e.target.value)}
                      required minLength={2} maxLength={120}
                      className="bg-slate-900/60 border-slate-600 text-white"
                    />
                  </div>

                  <div className="sm:col-span-2 space-y-1">
                    <Label className="text-slate-200" htmlFor="contact-email">Organisation contact email (optional)</Label>
                    <Input
                      id="contact-email" type="email"
                      data-testid="org-contact-email-input"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      className="bg-slate-900/60 border-slate-600 text-white"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-slate-200" htmlFor="admin-name">Your full name</Label>
                    <Input
                      id="admin-name"
                      data-testid="org-admin-name-input"
                      value={adminName}
                      onChange={(e) => setAdminName(e.target.value)}
                      required minLength={2}
                      className="bg-slate-900/60 border-slate-600 text-white"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-slate-200" htmlFor="admin-email">Your work email</Label>
                    <Input
                      id="admin-email" type="email"
                      data-testid="org-admin-email-input"
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      required
                      className="bg-slate-900/60 border-slate-600 text-white"
                    />
                  </div>

                  <div className="sm:col-span-2 space-y-1">
                    <Label className="text-slate-200" htmlFor="password">Choose a password (min 8 characters)</Label>
                    <Input
                      id="password" type="password"
                      data-testid="org-admin-password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required minLength={8}
                      className="bg-slate-900/60 border-slate-600 text-white"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="org-signup-submit"
                  className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating workspace…</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Create my Visitex workspace</>
                  )}
                </Button>

                <p className="text-xs text-slate-400 text-center">
                  By creating an account you agree to operate under your own
                  organisation&apos;s NDIS quality and safeguard responsibilities.
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
